import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Printer, Download, ArrowLeft, Pencil, FileText, Paperclip, ExternalLink, DownloadCloud } from "lucide-react";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function OAPreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [oa, setOa] = useState(null);
  const [html, setHtml] = useState("");
  const [busy, setBusy] = useState(false);
  const [annexures, setAnnexures] = useState([]);
  const [showAnx, setShowAnx] = useState(false);
  const iframeRef = useRef(null);

  const isAdmin = user.role === "admin";
  const perms = user.permissions || [];
  const canViewAnx = isAdmin || perms.includes("view_annexure");
  const canDownloadAnx = isAdmin || perms.includes("download_annexure");

  useEffect(() => {
    (async () => {
      try {
        const [{ data: o }, { data: doc }] = await Promise.all([
          api.get(`/oas/${id}`),
          api.get(`/oas/${id}/html`),
        ]);
        setOa(o);
        setHtml(typeof doc === "string" ? doc : "");
        if (canViewAnx) {
          try { const { data: anx } = await api.get(`/oas/${id}/annexures`); setAnnexures(anx); } catch { /* no access */ }
        }
      } catch (e) { toast.error(errMsg(e)); navigate("/oa"); }
    })();
  }, [id]); // eslint-disable-line

  const openAnx = async (a, download) => {
    try {
      const { data } = await api.get(`/documents/annexure/${a.id}/file`, { params: { mode: download ? "download" : "view" }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (download) { const el = document.createElement("a"); el.href = url; el.download = a.filename; el.click(); }
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const downloadAll = async () => { for (const a of annexures) { await openAnx(a, true); } };

  // Print the master document from the same HTML the PDF uses.
  const printDoc = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch {
      window.print();
    }
  };

  const downloadPDF = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/oas/${id}/pdf`, { responseType: "blob" });
      const safe = (oa.customerName || "Customer").replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "");
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${oa.oaNumber}-${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) { toast.error(errMsg(e, "PDF generation failed")); }
    setBusy(false);
  };

  const viewPO = async () => {
    try {
      const { data } = await api.get("/po/download", { params: { path: oa.poFile.storagePath, mode: "view" }, responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!oa) return <div className="p-8 text-slate-500">Loading OA…</div>;
  const canEdit = isAdmin || (perms.includes("edit_oa") && !["generated", "completed"].includes(oa.status));
  const canPrint = isAdmin || perms.includes("print_oa");
  const canPdf = isAdmin || perms.includes("download_pdf");

  return (
    <div className="min-h-screen bg-slate-200">
      <div className="no-print sticky top-0 z-20 bg-slate-900 text-white px-6 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back</button>
        <div className="font-head font-semibold">Preview · <span className="font-mono">{oa.oaNumber}</span></div>
        <div className="flex items-center gap-2">
          {oa.poFile && <button className="btn-outline btn-sm" onClick={viewPO} data-testid="view-po-btn"><FileText className="h-4 w-4" /> View PO</button>}
          {canEdit && <button className="btn-outline btn-sm" onClick={() => navigate(`/oa/${id}/edit`)} data-testid="preview-edit-btn"><Pencil className="h-4 w-4" /> Edit</button>}
          {canPrint && <button className="btn-outline btn-sm" onClick={printDoc} data-testid="print-oa-btn"><Printer className="h-4 w-4" /> Print</button>}
          {canPdf && <button className="btn-accent btn-sm" onClick={downloadPDF} disabled={busy} data-testid="download-pdf-btn"><Download className="h-4 w-4" /> {busy ? "…" : "Download PDF"}</button>}
        </div>
      </div>

      {canViewAnx && annexures.length > 0 && (
        <div className="no-print bg-slate-800 text-slate-100 px-6 py-2.5" data-testid="oa-annexure-bar">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <button className="flex items-center gap-2 text-sm font-medium" onClick={() => setShowAnx((v) => !v)} data-testid="oa-annexure-toggle">
              <Paperclip className="h-4 w-4 text-amber-400" /> Annexure
              <span className="badge bg-amber-500/20 text-amber-300 border border-amber-500/30" data-testid="oa-annexure-count">{annexures.length} File{annexures.length > 1 ? "s" : ""}</span>
              <span className="text-xs text-slate-400">{showAnx ? "Hide" : "View All"}</span>
            </button>
            {canDownloadAnx && <button className="btn-outline btn-sm border-slate-600 text-slate-200 hover:bg-white/10" onClick={downloadAll} data-testid="oa-annexure-download-all"><DownloadCloud className="h-4 w-4" /> Download All</button>}
          </div>
          {showAnx && (
            <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="oa-annexure-list">
              {annexures.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-2 bg-slate-700/60 rounded-lg px-3 py-2 text-sm">
                  <span className="truncate flex items-center gap-2"><FileText className="h-4 w-4 text-slate-400 shrink-0" /> {a.filename}</span>
                  <span className="flex items-center gap-1 shrink-0">
                    <button className="p-1 rounded hover:bg-white/10" title="View" onClick={() => openAnx(a, false)} data-testid={`oa-annexure-view-${a.id}`}><ExternalLink className="h-4 w-4" /></button>
                    {canDownloadAnx && <button className="p-1 rounded hover:bg-white/10" title="Download" onClick={() => openAnx(a, true)} data-testid={`oa-annexure-download-${a.id}`}><Download className="h-4 w-4" /></button>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="py-8 flex justify-center overflow-x-auto">
        <iframe
          ref={iframeRef}
          title="oa-document"
          data-testid="oa-document"
          srcDoc={html}
          className="bg-white shadow-2xl"
          style={{ width: "210mm", height: "297mm", border: "none" }}
        />
      </div>
    </div>
  );
}
